import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Reveals its children with a soft rise the first time they scroll into
 * view. Reduced-motion users get the content immediately (handled in CSS).
 *
 * With `stage`, the wrapper doesn't fade itself: it only flips `reveal-in`,
 * so children with their own `.reveal-in …` rules orchestrate the entrance.
 * Use it when the parts should animate individually — a wrapper fade on top
 * would multiply into the children's opacity and wash out their stagger.
 */
export function Reveal({
  children,
  className,
  delay = 0,
  stage = false,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  stage?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Fail open: if the environment can't do viewport intersection (no IO, or
    // a degenerate zero-height viewport as in some embedded webviews), show
    // everything immediately rather than leaving the page invisible.
    if (typeof IntersectionObserver === "undefined" || window.innerHeight === 0) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={cn(stage ? "reveal-stage" : "reveal", shown && "reveal-in", className)}
      style={stage ? undefined : { transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}
